// client/src/components/event/PollQuizConfig.jsx
/**
 * =================================================================
 * Poll / Quiz Configuration Component
 * =================================================================
 * Follows ChallengeConfig pattern for consistency.
 * FEATURES:
 * 1. Set type (Poll/Quiz), questions JSON
 * 2. Schedule start/end date
 * 3. Display shareable hyperlink
 * 4. View responses with scores
 * =================================================================
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./AuctionConfig.css";

const PollQuizConfig = ({ tenantId, eventId, itemId, itemName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const [config, setConfig] = useState({
    name: itemName || "Poll",
    type: "POLL",
    description: "",
    questions: [],
    start_at: "",
    end_at: "",
    status: "NEW",
  });

  // Question builder state
  const [newQuestion, setNewQuestion] = useState("");
  const [newOptions, setNewOptions] = useState(["", ""]);
  const [newCorrectAnswer, setNewCorrectAnswer] = useState("");

  const [responses, setResponses] = useState([]);
  const [showResponses, setShowResponses] = useState(false);
  const [responseCount, setResponseCount] = useState(0);
  const [configExists, setConfigExists] = useState(false);

  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const shareLink = configExists
    ? `${window.location.origin}/${tenantId}/${eventId}/${itemId}/action?type=poll_quiz`
    : "";

  useEffect(() => {
    loadConfig();
  }, [tenantId, eventId, itemId]);

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateTimeForBackend = (val) => {
    if (!val) return null;
    try {
      const d = new Date(val);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch {
      return null;
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.get(
        `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/config`
      );

      if (response.data.success && response.data.poll_quiz) {
        const c = response.data.poll_quiz;
        setConfigExists(true);

        let parsedQuestions = c.questions || [];
        if (typeof parsedQuestions === "string") {
          try { parsedQuestions = JSON.parse(parsedQuestions); } catch { parsedQuestions = []; }
        }

        setConfig({
          name: c.name || itemName || "Poll",
          type: c.type || "POLL",
          description: c.description || "",
          questions: parsedQuestions,
          start_at: formatDateForInput(c.start_at),
          end_at: formatDateForInput(c.end_at),
          status: c.status || "NEW",
        });
        loadResponseCount();
      } else {
        setConfigExists(false);
        setMessage({
          text: "No Poll/Quiz configured yet. Fill out the form below to create one.",
          type: "info",
        });
      }
    } catch (err) {
      console.error("Error loading Poll/Quiz config:", err);
      if (err.response?.status === 404) {
        setMessage({
          text: "No Poll/Quiz configured yet. Fill out the form below to create one.",
          type: "info",
        });
      } else {
        setMessage({ text: "Failed to load Poll/Quiz configuration", type: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadResponseCount = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/responses`
      );
      if (response.data.success) {
        setResponseCount(response.data.count || response.data.responses?.length || 0);
      }
    } catch { /* Non-critical */ }
  };

  const loadResponses = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/responses`
      );
      if (response.data.success) {
        setResponses(response.data.responses);
        setResponseCount(response.data.count || response.data.responses.length);
        setShowResponses(true);
      }
    } catch (err) {
      console.error("Error loading responses:", err);
      setMessage({ text: "Failed to load responses", type: "error" });
    }
  };

  const handleInputChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  // Question builder
  const addQuestion = () => {
    if (!newQuestion.trim()) {
      setMessage({ text: "Question text is required", type: "error" });
      return;
    }
    const filteredOptions = newOptions.filter(o => o.trim());
    if (filteredOptions.length < 2) {
      setMessage({ text: "At least 2 options are required", type: "error" });
      return;
    }

    const q = {
      id: Date.now(),
      question: newQuestion.trim(),
      options: filteredOptions,
      correct_answer: config.type === "QUIZ" ? newCorrectAnswer.trim() || null : null,
    };

    setConfig(prev => ({
      ...prev,
      questions: [...prev.questions, q],
    }));
    setNewQuestion("");
    setNewOptions(["", ""]);
    setNewCorrectAnswer("");
    setMessage({ text: "", type: "" });
  };

  const removeQuestion = (index) => {
    setConfig(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const updateOption = (index, value) => {
    setNewOptions(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addOptionField = () => {
    if (newOptions.length < 6) {
      setNewOptions(prev => [...prev, ""]);
    }
  };

  const validateConfig = () => {
    const errors = [];
    if (!config.name || config.name.trim().length === 0) {
      errors.push("Name is required");
    }
    if (config.start_at && config.end_at) {
      if (new Date(config.end_at) <= new Date(config.start_at)) {
        errors.push("End date must be after start date");
      }
    }
    return errors;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const errors = validateConfig();
    if (errors.length > 0) {
      setMessage({ text: errors.join(". "), type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: config.name,
        type: config.type,
        description: config.description || null,
        questions: config.questions,
        start_at: formatDateTimeForBackend(config.start_at),
        end_at: formatDateTimeForBackend(config.end_at),
        status: config.status,
      };

      const response = await axios.post(
        `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/config`,
        payload
      );

      if (response.data.success) {
        setConfigExists(true);
        setMessage({ text: "Poll/Quiz configuration saved successfully!", type: "success" });
        setTimeout(() => {
          setMessage({ text: "", type: "" });
          loadConfig();
        }, 2000);
      }
    } catch (err) {
      console.error("Error saving Poll/Quiz config:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to save Poll/Quiz configuration",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(
      () => {
        setMessage({ text: "Link copied to clipboard!", type: "success" });
        setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      },
      () => setMessage({ text: "Failed to copy link", type: "error" })
    );
  };

  const handleDelete = async () => {
    if (!window.confirm("Cancel this Poll/Quiz? This cannot be undone.")) return;

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const response = await axios.post(
        `${baseUrl}/api/poll-quiz/${tenantId}/event/${eventId}/item/${itemId}/config`,
        { ...config, status: "CANCELLED" }
      );
      if (response.data.success) {
        setMessage({ text: "Poll/Quiz cancelled successfully", type: "success" });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error cancelling Poll/Quiz:", err);
      setMessage({
        text: err.response?.data?.message || "Failed to cancel Poll/Quiz",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const formatResponses = (resp) => {
    if (!resp) return "—";
    if (typeof resp === "string") {
      try { resp = JSON.parse(resp); } catch { return resp; }
    }
    if (typeof resp === "object") {
      return Object.entries(resp).map(([k, v]) => `${k}: ${v}`).join(", ");
    }
    return String(resp);
  };

  if (loading) {
    return (
      <div className="auction-config-container">
        <div className="loading">Loading Poll/Quiz configuration...</div>
      </div>
    );
  }

  const isComplete = config.status === "COMPLETE" || config.status === "CANCELLED";
  const typeLabel = config.type === "QUIZ" ? "Quiz" : "Poll";

  return (
    <div className="auction-config-container">
      <div className="auction-config-header">
        <h2>{typeLabel} Configuration</h2>
        <button className="close-button" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {shareLink && (
        <div className="share-link-section">
          <h3>Shareable Link</h3>
          <p className="share-link-description">
            Share this link for participants to respond:
          </p>
          <div className="share-link-container">
            <div className="share-link-display">
              <a href={shareLink} target="_blank" rel="noopener noreferrer">
                {shareLink}
              </a>
            </div>
            <button type="button" className="copy-link-button" onClick={copyShareLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      {configExists && (
        <div className="auction-stats-section">
          <h3>{typeLabel} Statistics</h3>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Responses</div>
              <div className="stat-value">{responseCount}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Questions</div>
              <div className="stat-value">{config.questions.length}</div>
            </div>
          </div>
          <div className="stats-actions">
            <button type="button" className="view-entries-button" onClick={loadResponses}>
              View Responses
            </button>
          </div>
        </div>
      )}

      <form className="auction-config-form" onSubmit={handleSave}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={config.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Enter name"
              disabled={isComplete}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="type">Type</label>
            <select
              id="type"
              value={config.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              disabled={isComplete}
            >
              <option value="POLL">Poll</option>
              <option value="QUIZ">Quiz</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={config.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the poll/quiz"
            disabled={isComplete}
            rows={2}
          />
        </div>

        {/* Questions List */}
        {config.questions.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h4 style={{ margin: "0 0 8px" }}>Questions ({config.questions.length})</h4>
            {config.questions.map((q, index) => (
              <div key={q.id || index} style={{
                padding: "8px 12px", marginBottom: 8,
                background: "#f5f5f5", borderRadius: 4, border: "1px solid #ddd",
                display: "flex", justifyContent: "space-between", alignItems: "flex-start"
              }}>
                <div>
                  <strong>Q{index + 1}:</strong> {q.question}
                  <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                    Options: {(q.options || []).join(" | ")}
                    {q.correct_answer && <> &mdash; Answer: {q.correct_answer}</>}
                  </div>
                </div>
                {!isComplete && (
                  <button
                    type="button"
                    onClick={() => removeQuestion(index)}
                    style={{ background: "none", border: "none", color: "#dc3545", cursor: "pointer", fontSize: 16 }}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Add Question */}
        {!isComplete && (
          <div style={{ padding: 12, background: "#f9f9f9", borderRadius: 4, marginBottom: 16, border: "1px solid #e0e0e0" }}>
            <h4 style={{ margin: "0 0 8px" }}>Add Question</h4>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Enter question text"
              />
            </div>
            {newOptions.map((opt, i) => (
              <div key={i} className="form-group" style={{ marginBottom: 4 }}>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => updateOption(i, e.target.value)}
                  placeholder={`Option ${i + 1}`}
                />
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {newOptions.length < 6 && (
                <button type="button" onClick={addOptionField} style={{
                  padding: "4px 12px", fontSize: 12, background: "#f0f0f0",
                  border: "1px solid #ccc", borderRadius: 4, cursor: "pointer"
                }}>
                  + Option
                </button>
              )}
            </div>
            {config.type === "QUIZ" && (
              <div className="form-group" style={{ marginTop: 8 }}>
                <input
                  type="text"
                  value={newCorrectAnswer}
                  onChange={(e) => setNewCorrectAnswer(e.target.value)}
                  placeholder="Correct answer (must match an option exactly)"
                />
              </div>
            )}
            <button
              type="button"
              onClick={addQuestion}
              className="save-button"
              style={{ marginTop: 8, padding: "6px 16px", fontSize: 13 }}
            >
              Add Question
            </button>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_at">Start Date & Time</label>
            <input
              type="datetime-local"
              id="start_at"
              value={config.start_at}
              onChange={(e) => handleInputChange("start_at", e.target.value)}
              disabled={isComplete}
            />
          </div>
          <div className="form-group">
            <label htmlFor="end_at">End Date & Time</label>
            <input
              type="datetime-local"
              id="end_at"
              value={config.end_at}
              onChange={(e) => handleInputChange("end_at", e.target.value)}
              disabled={isComplete}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            value={config.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            disabled={isComplete}
          >
            <option value="NEW">New</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETE">Complete</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-actions">
          {isComplete ? (
            <div className="completion-notice">
              This {typeLabel.toLowerCase()} is {config.status === "CANCELLED" ? "cancelled" : "complete"} - no further changes allowed
            </div>
          ) : (
            <>
              <button type="submit" className="save-button" disabled={saving}>
                {saving ? "Saving..." : "Save Configuration"}
              </button>
              {configExists && (
                <button
                  type="button"
                  className="delete-button"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Cancel {typeLabel}
                </button>
              )}
            </>
          )}
        </div>
      </form>

      {showResponses && (
        <div className="modal-overlay" onClick={() => setShowResponses(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Responses ({responses.length})</h3>
              <button className="close-button" onClick={() => setShowResponses(false)}>
                &times;
              </button>
            </div>
            <div className="modal-body">
              <table className="entries-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Responses</th>
                    {config.type === "QUIZ" && <th>Score</th>}
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {responses.map((resp, index) => (
                    <tr key={resp.id}>
                      <td>{index + 1}</td>
                      <td>{resp.name}</td>
                      <td>{resp.email}</td>
                      <td>{formatResponses(resp.responses)}</td>
                      {config.type === "QUIZ" && <td>{resp.score ?? "—"}</td>}
                      <td>{new Date(resp.created_on).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollQuizConfig;
